import React from "react";
import { Tooltip } from "@mui/material";
import type { DataTableColumn } from "../components/EmailDataTableComponent/DataTableComponent";
import type { ShipmentApi, ReferenceNames } from "../../../src/shipments/types";
import type { ShipmentUI } from "../store/shipmentSlice";
import { labelFromPropertyName } from "@/admin/shipments/shipmentPropertiesUtils";
import { formatDateToDisplayIgnoreTZ } from "@/utils/shipmentDateFormatting";
import { AssigneeCell } from "./AssigneeCell";
import { ReminderCell } from "./ReminderCell";
import { StatusBadge } from "./components/StatusBadge";

// Helper function to get property values by name from a shipment's properties
const getPropertyValues = (
  shipment: ShipmentUI,
  propertyName: ReferenceNames
): string => {
  const matchingProperties = shipment.properties.filter(
    (prop) => prop.name === propertyName
  );
  if (matchingProperties.length === 0) return "";

  // Get unique values and join with commas
  const uniqueValues = Array.from(
    new Set(matchingProperties.map((prop) => prop.value))
  );
  return uniqueValues.join(", ");
};

// Helper function to render container numbers with tooltip for overflow
const renderContainerNumbers = (shipment: ShipmentUI) => {
  const matchingProperties = shipment.properties.filter(
    (prop) => prop.name === "container_number"
  );
  if (matchingProperties.length === 0) return "";

  // Get unique values
  const uniqueValues = Array.from(
    new Set(matchingProperties.map((prop) => prop.value))
  );

  if (uniqueValues.length <= 2) {
    return uniqueValues.join(", ");
  }

  // Show first two and indicate there are more
  const displayText = `${uniqueValues.slice(0, 1).join(", ")} `;
  const moreText = `(${uniqueValues.length - 1} more)`;
  const fullText = uniqueValues.join(", ");

  return (
    <span>
      {displayText}
      <Tooltip title={fullText} arrow>
        <span style={{ textDecoration: "underline", cursor: "pointer" }}>
          {moreText}
        </span>
      </Tooltip>
    </span>
  );
};

// Define columns based on property types from ReferenceNameEnum
export const shipmentsTableColumns: DataTableColumn<ShipmentUI>[] = [
  {
    columnName: "Assignees",
    renderFn: (shipment) => (
      <AssigneeCell
        shipmentId={shipment.id}
        assignedToIds={shipment.assignedToIds}
        isTemporary={shipment.isTemporary}
      />
    ),
    clipText: false,
    flex: 2,
    visibleByDefault: false,
  },
  {
    columnName: "Exceptions",
    renderFn: (shipment) => (
      <ReminderCell
        shipmentId={shipment.id}
        unresolvedExceptions={shipment.unresolvedExceptions}
        unresolvedMilestones={shipment.unresolvedMilestones}
      />
    ),
    clipText: false,
    flex: 1,
    visibleByDefault: true,
    editable: false,
  },
  {
    columnName: labelFromPropertyName("booking_number"),
    fieldName: "booking_number" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "booking_number"),
    valueFn: (shipment) => getPropertyValues(shipment, "booking_number"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("shipper"),
    fieldName: "shipper" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "shipper"),
    clipText: true,
    flex: 2,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("consignee"),
    fieldName: "consignee" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "consignee"),
    clipText: true,
    flex: 2,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("job_number"),
    fieldName: "job_number" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "job_number"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: false,
  },
  {
    columnName: "House BL",
    fieldName: "house_bill_of_lading" as any,
    valueFn: (shipment) => getPropertyValues(shipment, "house_bill_of_lading"),
    flex: 2,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: "Master BL",
    fieldName: "master_bill_of_lading" as any,
    valueFn: (shipment) => getPropertyValues(shipment, "master_bill_of_lading"),
    flex: 2,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: "Reference Number",
    fieldName: "reference_number" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "reference_number"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("port_of_loading"),
    fieldName: "port_of_loading" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "port_of_loading"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("port_of_discharge"),
    fieldName: "port_of_discharge" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "port_of_discharge"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("origin"),
    fieldName: "origin" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "origin"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("destination"),
    fieldName: "destination" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "destination"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("container_number"),
    fieldName: "container_number" as any,
    renderFn: renderContainerNumbers,
    valueFn: (shipment) => getPropertyValues(shipment, "container_number"),
    clipText: false,
    width: 200,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("doc_cut_off_date"),
    fieldName: "doc_cut_off_date" as any,
    renderFn: (shipment) => {
      const docCutOffDate = getPropertyValues(shipment, "doc_cut_off_date");
      if (!docCutOffDate) return "-";
      return formatDateToDisplayIgnoreTZ(docCutOffDate);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "doc_cut_off_date"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("port_cut_off_date"),
    fieldName: "port_cut_off_date" as any,
    renderFn: (shipment) => {
      const portCutOffDate = getPropertyValues(shipment, "port_cut_off_date");
      if (!portCutOffDate) return "-";
      return formatDateToDisplayIgnoreTZ(portCutOffDate);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "port_cut_off_date"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("etd"),
    fieldName: "etd" as any,
    renderFn: (shipment) => {
      const etd = getPropertyValues(shipment, "etd");
      if (!etd) return "-";
      return formatDateToDisplayIgnoreTZ(etd);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "etd"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("atd"),
    fieldName: "atd" as any,
    renderFn: (shipment) => {
      const atd = getPropertyValues(shipment, "atd");
      if (!atd) return "-";
      return formatDateToDisplayIgnoreTZ(atd);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "atd"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("eta"),
    fieldName: "eta" as any,
    renderFn: (shipment) => {
      const eta = getPropertyValues(shipment, "eta");
      if (!eta) return "-";
      return formatDateToDisplayIgnoreTZ(eta);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "eta"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("ata"),
    fieldName: "ata" as any,
    renderFn: (shipment) => {
      const atd = getPropertyValues(shipment, "ata");
      if (!atd) return "-";
      return formatDateToDisplayIgnoreTZ(atd);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "ata"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("gated_out_date"),
    fieldName: "gated_out_date" as any,
    renderFn: (shipment) => {
      const gatedOutDate = getPropertyValues(shipment, "gated_out_date");
      if (!gatedOutDate) return "-";
      return formatDateToDisplayIgnoreTZ(gatedOutDate);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "gated_out_date"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("empty_return_date"),
    fieldName: "empty_return_date" as any,
    renderFn: (shipment) => {
      const emptyReturnDate = getPropertyValues(shipment, "empty_return_date");
      if (!emptyReturnDate) return "-";
      return formatDateToDisplayIgnoreTZ(emptyReturnDate);
    },
    valueFn: (shipment) => getPropertyValues(shipment, "empty_return_date"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  // {
  //   columnName: "BL Create Date",
  //   fieldName: "createdAt" as any,
  //   renderFn: (shipment) =>
  //     formatDateValue(
  //       shipment.createdAt instanceof Date
  //         ? shipment.createdAt.toISOString()
  //         : shipment.createdAt
  //     ),
  //   valueFn: (shipment) =>
  //     shipment.createdAt instanceof Date
  //       ? shipment.createdAt.toISOString()
  //       : shipment.createdAt,
  //   clipText: true,
  //   flex: 1.5,
  //   visibleByDefault: true,
  //   editable: true,
  // },
  {
    columnName: labelFromPropertyName("shipping_line"),
    fieldName: "shipping_line" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "shipping_line"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("carrier"),
    fieldName: "carrier" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "carrier"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("vessel_name"),
    fieldName: "vessel_name" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "vessel_name"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("voyage_number"),
    fieldName: "voyage_number" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "voyage_number"),
    clipText: true,
    flex: 1,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("release_number"),
    fieldName: "release_number" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "release_number"),
    clipText: true,
    flex: 1.5,
    editable: true,
    visibleByDefault: false,
  },
  {
    columnName: labelFromPropertyName("container_type"),
    fieldName: "container_type" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "container_type"),
    clipText: true,
    flex: 1,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("seal_numbers"),
    fieldName: "seal_numbers" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "seal_numbers"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("number_of_containers"),
    fieldName: "number_of_containers" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "number_of_containers"),
    clipText: true,
    flex: 1,
    visibleByDefault: false,
    editable: true,
  },
  {
    columnName: labelFromPropertyName("invoice_number"),
    fieldName: "invoice_number" as any,
    renderFn: (shipment) => getPropertyValues(shipment, "invoice_number"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: false,
    editable: true,
  },
  // {
  //   columnName: formatPropertyName("unknown_id"),
  //   renderFn: (shipment) => getPropertyValues(shipment, "unknown_id"),
  //   clipText: true,
  //   flex: 1.5,
  //   visibleByDefault: false,
  // },
];
